import React, { useCallback, useEffect, useState } from 'react';
import { fetchTenantEmailToggles, updateTenantEmailToggles } from '../services/api';

/**
 * Per-tenant super-admin toggles. Started as email-only (2026-08-31); extended
 * 2026-10-23 to also cover AI-spend kill switches (transcription + analyzer)
 * so one tenant can be opted out without touching the global env flags.
 *
 * Route: /super/tenant-email  (SuperAdminRoute — kept for URL stability)
 */

type ToggleKey =
  | 'reports_email_enabled'
  | 'cost_alerts_email_enabled'
  | 'transcription_enabled'
  | 'analyzer_enabled';

interface TenantRow {
  id: number;
  name: string;
  slug: string;
  reports_email_enabled: boolean;
  cost_alerts_email_enabled: boolean;
  transcription_enabled: boolean;
  analyzer_enabled: boolean;
}

const COLUMNS: Array<{ key: ToggleKey; label: string; hint: string }> = [
  { key: 'reports_email_enabled',     label: 'Daily / Weekly Report', hint: 'Scheduled performance email' },
  { key: 'cost_alerts_email_enabled', label: 'Cost Alerts',           hint: 'Weekly AI-spend breach mail' },
  { key: 'transcription_enabled',     label: 'Transcription',         hint: 'Deepgram transcription on SIM calls' },
  { key: 'analyzer_enabled',          label: 'Analyzer',              hint: 'Summary + skills AI pipeline' },
];

const card: React.CSSProperties = {
  background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10,
  padding: 20, marginBottom: 16,
};
const th: React.CSSProperties = {
  textAlign: 'left', padding: '10px 12px', fontSize: 12,
  color: '#6b7280', fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.05em', borderBottom: '1px solid var(--border)',
};
const td: React.CSSProperties = {
  padding: '12px', borderBottom: '1px solid var(--border)', fontSize: 14,
};

const TenantEmailTogglesPage: React.FC = () => {
  const [rows, setRows] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await fetchTenantEmailToggles();
      setRows(data.tenants ?? []);
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (tenantId: number, key: ToggleKey, value: boolean) => {
    const busyKey = `${tenantId}:${key}`;
    setBusy(prev => ({ ...prev, [busyKey]: true }));
    // Optimistic update — roll back on failure.
    setRows(rs => rs.map(r => r.id === tenantId ? { ...r, [key]: value } : r));
    try {
      await updateTenantEmailToggles(tenantId, { [key]: value });
    } catch (e: any) {
      setErr(e?.response?.data?.error ?? e?.message ?? 'Failed to save');
      setRows(rs => rs.map(r => r.id === tenantId ? { ...r, [key]: !value } : r));
    } finally {
      setBusy(prev => ({ ...prev, [busyKey]: false }));
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <h1 style={{ marginBottom: 8 }}>Tenant Toggles</h1>
      <p style={{ color: '#6b7280', marginBottom: 24 }}>
        Per-tenant switches for scheduled email and AI-spend features. Turning
        a switch OFF opts that tenant out without touching the global env flags
        (which apply to every tenant at once).
      </p>

      {err && (
        <div style={{ ...card, color: '#dc2626', borderColor: '#dc2626' }}>{err}</div>
      )}

      <div style={card}>
        {loading && <div style={{ color: '#6b7280' }}>Loading…</div>}
        {!loading && rows.length === 0 && (
          <div style={{ color: '#6b7280' }}>No active tenants.</div>
        )}
        {!loading && rows.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Tenant</th>
                {COLUMNS.map(col => (
                  <th key={col.key} style={th} title={col.hint}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{r.name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>#{r.id} · {r.slug}</div>
                  </td>
                  {COLUMNS.map(col => (
                    <td key={col.key} style={td}>
                      <Toggle
                        value={r[col.key]}
                        busy={!!busy[`${r.id}:${col.key}`]}
                        onChange={v => toggle(r.id, col.key, v)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ ...card, background: '#f9fafb', fontSize: 13, color: '#4b5563' }}>
        <strong>Note.</strong> Even with a tenant switch ON, email still respects
        the global env flags (<code>REPORTS_EMAIL_ENABLED</code>,{' '}
        <code>ANALYZER_COST_ALERT_ENABLED</code>,{' '}
        <code>EMAIL_SENDING_ENABLED</code>). The AI switches
        (Transcription, Analyzer) also respect their own global env flags
        (<code>ANALYZER_TIMELINE_PIPELINE_ENABLED</code>, Deepgram API key,
        etc.). If a tenant switch is OFF, that tenant is silenced regardless
        of any global setting.
      </div>
    </div>
  );
};

const Toggle: React.FC<{ value: boolean; busy: boolean; onChange: (v: boolean) => void }> = ({ value, busy, onChange }) => (
  <label style={{
    display: 'inline-flex', alignItems: 'center', gap: 8, cursor: busy ? 'default' : 'pointer',
    opacity: busy ? 0.6 : 1,
  }}>
    <span style={{
      display: 'inline-block', width: 36, height: 20, borderRadius: 999,
      background: value ? '#059669' : '#d1d5db',
      position: 'relative', transition: 'background 0.15s',
    }}>
      <span style={{
        position: 'absolute', top: 2, left: value ? 18 : 2,
        width: 16, height: 16, borderRadius: '50%', background: '#fff',
        transition: 'left 0.15s',
        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
      }} />
    </span>
    <span style={{ fontSize: 13, color: value ? '#059669' : '#6b7280' }}>
      {value ? 'ON' : 'OFF'}
    </span>
    <input
      type="checkbox"
      checked={value}
      disabled={busy}
      onChange={e => onChange(e.target.checked)}
      style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
    />
  </label>
);

export default TenantEmailTogglesPage;
